/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { NM_SORT_OPTIONS } from '@/lib/search/sort-options';
import NmMobileSortOverlay from './nm-mobile-sort-overlay';

/**
 * Is the bar currently PINNED? Returns a ref to put on the sticky element and a
 * boolean that flips exactly when it starts and stops sticking.
 *
 * WHY THIS EXISTS. Live NM paints a shadow under this bar only while it is
 * stuck — `box-shadow:0 2px 5px -1px #9e9e9e`, re-derived from live's own
 * shipped stylesheet and cited in full in theme/tokens/brand-nm/search.css.
 * Live can express that in CSS alone because its bar is `position:fixed`
 * toggled by a class from script. Ours is `position:sticky`, which exposes no
 * stuck selector to CSS, so the state has to be observed and stamped.
 *
 * WHY AN IntersectionObserver AND NOT A SCROLL HANDLER. A `scroll` listener on
 * an SRP runs on every frame of every scroll — on a results page that is the
 * longest-scrolling surface in the storefront — and reading a rect inside it
 * forces layout on the main thread. This observer runs ZERO code while
 * scrolling: the browser evaluates the intersection off the main thread and
 * calls back only at the two transitions, so the steady-state cost of a scroll
 * from the top of the page to the bottom is two callbacks, each of which sets
 * one boolean.
 *
 * HOW THE THRESHOLD IS BUILT. The root is shrunk from the top by the bar's own
 * resolved sticky offset plus one pixel, and grown at the bottom so the bottom
 * edge can never clip the target. That leaves the top edge as the only thing
 * that can take the bar out of full visibility — so "not fully visible" means
 * exactly "its top has reached the pin line", which is the definition of stuck.
 *
 * THE OFFSET IS READ, NOT ASSUMED, AND IT MOVES AFTER MOUNT. `header/index.tsx`
 * publishes `--header-height` onto the document element from a ResizeObserver,
 * so the value this bar pins at is 0 (the fallback) until the header has
 * measured itself and 95 at 390 once it has — both were observed on this route,
 * in that order, during one page load. An observer built against 0 and never
 * rebuilt would put its pin line 95px above where the bar actually stops, and
 * the bar would then be fully visible inside the shrunk root at every scroll
 * position: no transition, no shadow, and nothing anywhere reporting an error.
 *
 * So the rebuild watches BOTH things that can move the offset. The
 * MutationObserver is the precise one — it is aimed at the exact `style`
 * attribute the header writes that property to, so it fires when the number
 * changes and at no other time. `resize` covers the other route, a media query
 * changing this element's own sticky offset. Both are rare-fire, and the
 * rebuild is skipped outright when the offset is unchanged.
 *
 * The height guard is what keeps the attribute off the element at widths where
 * the bar is display:none — a hidden element reports ratio 0, which would
 * otherwise read as stuck.
 */
function useStuck(): { ref: RefObject<HTMLDivElement | null>; stuck: boolean } {
    const ref = useRef<HTMLDivElement>(null);
    const [stuck, setStuck] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;

        let observer: IntersectionObserver | undefined;
        let builtForOffset: number | undefined;

        const build = (): void => {
            const parsed = Number.parseFloat(getComputedStyle(el).top);
            const offset = Number.isFinite(parsed) ? parsed : 0;
            if (observer && offset === builtForOffset) return;
            observer?.disconnect();
            builtForOffset = offset;
            observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[entries.length - 1];
                    if (!entry) return;
                    setStuck(entry.boundingClientRect.height > 0 && entry.intersectionRatio < 1);
                },
                { threshold: [1], rootMargin: `-${offset + 1}px 0px 100000px 0px` }
            );
            observer.observe(el);
        };

        build();
        window.addEventListener('resize', build, { passive: true });
        const offsetSource = new MutationObserver(build);
        offsetSource.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style'],
        });
        return () => {
            window.removeEventListener('resize', build);
            offsetSource.disconnect();
            observer?.disconnect();
        };
    }, []);

    return { ref, stuck };
}

/**
 * NM mobile sticky bar (search-3a-comparison.md P5): Filter | live item count |
 * Sort By in one bar, sticky over the grid — live's `grid-facet-buttons-sticky`.
 * Replaces the scrolled-away FILTERS button on the NM SRP's mobile profile
 * (the component itself is `md:hidden`; the desktop rail is always open, P3).
 *
 * Sticky offset: the SRP header is sticky (blast-3 `header-wrapper--sticky`),
 * so the bar pins directly below it via the header-maintained --header-height.
 *
 * THE BAR'S BOX IS NOT A UTILITY CLASS. It runs the full page width with its
 * content inset, which on this storefront means cancelling `.srp-frame`'s
 * inline padding — a DERIVED bleed, in `.srp-sticky-bar`
 * (theme/overrides/search.css). It was a literal `-mx-4` against a 10px frame
 * until 2026-08-19, which put 6px of the strip outside the viewport and made
 * the whole SRP scroll sideways at 375, 390 and 440.
 *
 * NM-ONLY BY MOUNT, and that is why nothing here is brand-keyed. The route
 * renders this behind `ux.mobileStickyBar`, which is `true` for NM and `false`
 * for BG (SEARCH_UX_BY_BRAND, lib/search/search-ux.ts). BG's own mobile SRP
 * captures back that: bg-search-m-375, bg-search-m-440 and bg-search-color-375
 * contain no sort affordance at all, and the mobile filter chrome BG does have
 * is a full-screen drill-down of 54px rows (bg-search-color-375:99-125), not a
 * bar. A BG arm here would be unreachable code standing on no capture.
 */
export default function SearchStickyBar({
    total,
    onToggleFilters,
    filtersOpen,
    filtersTriggerRef,
    serverSortOptions,
    surface = 'search',
}: {
    total: number;
    onToggleFilters: () => void;
    filtersOpen: boolean;
    /**
     * Handed down so the filter pane can restore focus HERE when it closes.
     * Radix restores focus to whatever held it before the pane opened, and on
     * macOS and on touch a click does not focus a `<button>` — so at the moment
     * of opening `document.activeElement` is the body and there is nothing to
     * restore TO. The pane intercepts `onCloseAutoFocus` and focuses this ref by
     * hand; without it the shopper's place in the page is lost on Escape. The
     * mobile SORT pane one row over solves the same problem with its own local
     * ref, which it can do because its trigger is inside that component; this
     * trigger's pane is mounted by the route, so the ref has to travel.
     * Optional, so the bar's other mounts and its own tests are unaffected.
     */
    filtersTriggerRef?: RefObject<HTMLButtonElement | null>;
    serverSortOptions?: Array<{ id?: string; label?: string }>;
    /**
     * Which surface this bar is serving. Controls the thousands-separator on the
     * item count:
     *
     * - `'search'` — live NM SRP omits the separator (`11187 Items`), confirmed
     *   at .reference/captures/search-m-390/App.tsx:225 and corroborated by the
     *   owner's nm-search-390.png where five digit glyphs paint with no
     *   comma-shaped ink between them.
     * - `'plp'` — live NM PLP formats with a separator (`23,217 Items`),
     *   confirmed at .reference/plp-womens-clothing.desktop.html:
     *   `.product-list__header__items`.
     *
     * The bar renders on both routes (routes/_app.search.tsx and
     * routes/_app.category.$categoryId.tsx), so the caller must pass the
     * surface rather than hard-coding it here.
     */
    surface?: 'search' | 'plp';
}): ReactElement {
    const { t } = useTranslation('search');
    const { ref: barRef, stuck } = useStuck();

    return (
        <div
            ref={barRef}
            data-testid="search-sticky-bar"
            /* Present ONLY while stuck, never as `false`. The at-rest DOM is
               therefore byte-identical to what it was before the shadow landed,
               which keeps the server render and every existing assertion about
               this element unchanged, and lets the stylesheet gate on the bare
               attribute rather than on its value. */
            data-stuck={stuck ? '' : undefined}
            className="srp-sticky-bar md:hidden sticky top-(--header-height,0px) z-30 flex items-center justify-between bg-background">
            {/* TWO groups, not three children. Live's bar splits at the top
                level into a filter group and a sort group, and the COUNT is
                inside the filter group — `div.mobile-filter-item.filter-option`
                holds `div.mobile-filter` (icon + button) and
                `div.total-item-count` as siblings
                — and the nesting is READ, not inferred, at
                `.reference/captures/search-m-390/App.tsx:219-232`: one
                `justify-between` row holding exactly two `items-center flex`
                wrappers, the first of which contains the icon+button group AND
                the `10948 Items` div. The barred nm-search-m-440 dump was the
                original citation here; it is dropped, and the capture carries
                the whole structural claim on its own. Byte-identical in
                search-m-375 and search-m-440 — no responsive tier inside the
                mobile range.

                CORROBORATED IN THE OWNER'S PIXELS, and one number corrected.
                `.reference/live-shots/nm-search-390.png`, converted to sRGB,
                scale 2.0 px/CSS px, page origin x=602/y=238: the filter group's
                ink runs x=12.0 → 154.0 with the count's first digit at x=78.5.
                That reproduces the capture's box arithmetic exactly — icon box
                at 10 (18px glyph, ink 12), button `pl-1` → "Filter" ink at
                32.5, `pr-[15px]` past ink-end 62 → count box at 77. The count
                therefore sits hard against the Filter label on the leading
                edge. This comment previously read "the group runs left 10 →
                158.3 with the count starting at 81", measured off the dump; the
                pixels put the count box at 77, so the 81 is retired.

                As three `justify-between` children the count was pushed to the
                middle of the bar instead — 158px from the leading edge at 390
                against live's 71px — which is why the grouping is structural
                and not a gap value. */}
            <div className="flex items-center" data-testid="search-sticky-filter-group">
                <StickyFilterIcon />
                {/* TYPE COMES FROM `--srp-sticky-filter-*`, NOT the shared
                    label-medium role. Live's button is
                    `text-[14px] tracking-[0.1px] leading-[24px]`, byte-identical
                    across all six NM mobile captures (search-m-375, -390, -440,
                    search-m-filter-375, search-m-filters-440, search-m-cats-375);
                    `type-label-medium` resolves 14px / 20px / 0.16px, so it
                    agreed on the size and on neither of the other two. Same
                    spelling as the sort button below, which already reads its
                    own `--srp-sticky-sort-*` set for the same reason. */}
                <Button
                    variant="ghost"
                    ref={filtersTriggerRef}
                    onClick={onToggleFilters}
                    aria-haspopup="dialog"
                    aria-expanded={filtersOpen}
                    aria-pressed={filtersOpen}
                    data-testid="search-sticky-filter"
                    className="srp-sticky-bar-filter text-(length:--srp-sticky-filter-font-size) leading-(--srp-sticky-filter-line-height) tracking-(--srp-sticky-filter-letter-spacing) font-bold">
                    {t('stickyBar.filter', { defaultValue: 'Filter' })}
                </Button>
                <span
                    className="text-(length:--typography-label-medium-font-size) tracking-(--typography-label-medium-letter-spacing) shrink-0 leading-(--srp-sticky-bar-count-line-height) text-(color:--srp-sticky-bar-count-color)"
                    data-testid="search-sticky-count">
                    {/* "Items", capital I on both surfaces. Thousands separator
                        varies by surface — see the `surface` prop above.
                        The CASE is identical: live's SRP shows `11187 Items`
                        and live's PLP shows `23,217 Items`
                        (.reference/captures/search-m-390/App.tsx:225 and
                        .reference/plp-womens-clothing.desktop.html
                        `.product-list__header__items`).

                        COLOUR: `--srp-sticky-bar-count-color` (rgb(92,92,92)
                        in brand-nm/search.css), NOT `--muted-foreground` which
                        is rgb(53,53,59) — live's header/rail foreground, not
                        the count's own colour.

                        LINE-HEIGHT: `--srp-sticky-bar-count-line-height` (21px)
                        from `leading-[21px]` at search-m-390/App.tsx:225.
                        The shared `--typography-label-medium-line-height` is
                        20px, one pixel short. */}
                    {surface === 'search'
                        ? t('stickyBar.itemsCountSearch', {
                              defaultValue: '{{count}} Items',
                              defaultValue_one: '{{count}} Item',
                              defaultValue_other: '{{count}} Items',
                              count: total,
                          })
                        : t('stickyBar.itemsCount', {
                              defaultValue: '{{count, number}} Items',
                              defaultValue_one: '{{count, number}} Item',
                              defaultValue_other: '{{count, number}} Items',
                              count: total,
                          })}
                </span>
            </div>
            {/* The trigger and the left panel it opens are ONE component: the
                panel's selected row is the trigger's state. See
                nm-mobile-sort-overlay.tsx for live's measured panel. */}
            <NmMobileSortOverlay serverOptions={serverSortOptions} fallbackOptions={NM_SORT_OPTIONS} />
        </div>
    );
}

/**
 * The 18x18 glyph that opens live's filter panel, which this bar had no
 * counterpart for at all.
 *
 * Unlike the sort chevron below, this one is RECOVERABLE, and WITHOUT the
 * barred dump: the decompiler rehosts live's own asset bytes, so the `<img
 * alt="filter icon">` at `.reference/captures/search-m-390/App.tsx:222` points
 * at a fetchable copy of the file live served. Fetched 2026-08-19, it is a
 * 24-viewBox three-bar filter mark —
 * `M10 18v-2h4v2h-4zm-4-5v-2h12v2H6zM3 8V6h18v2H3z` — wrapped in a full-square
 * `mask-type:alpha` mask over `path fill="#D9D9D9" d="M0 0h24v24H0z"`, which
 * covers the whole viewBox, changes nothing, and is dropped. The path below is
 * therefore live's own rather than a re-boxed house glyph. (The original
 * citation was the dump's inline `data:image/svg+xml;base64` on
 * `img.mobile-filter-icon`; same bytes, barred source, so it is dropped.)
 * Live paints it at #1C1B1F and sizes it down to 18 with its own class
 * (`w-[18px] h-[18px]`, search-m-390/App.tsx:222); `currentColor` inherits the
 * bar's foreground instead of pinning that near-black, so the glyph follows
 * the bar's ground rather than fighting a theme change.
 *
 * Decorative: the adjacent button already carries the accessible name.
 */
function StickyFilterIcon(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="srp-sticky-bar-filter-icon shrink-0">
            <path d="M10 18v-2h4v2h-4zm-4-5v-2h12v2H6zM3 8V6h18v2H3z" />
        </svg>
    );
}
